import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import _ from "lodash";
import Modal from "react-modal";
import {isMobile} from "../../../App";
import paths from "../../../paths";
import {auth} from "../../../store/auth.store";
import {CurrencyFormat, dateTimeStringFormat, mobileView, preventBetSlipValues} from "../../../services/misc.functions";
import {modalServiceSubject} from "../../../services/modal.service";
import {
    staleSummaryText,
    summarizeStale,
} from "features/betslip/lib/selection-guard";
import {
    isMutualItemPlayable,
    staleReasonOf,
    MutualBetslip,
    MutualSlipItem,
    MutualSlipOptions,
    useMutualBetslip,
    useMutualSlipOptions,
} from "features/betslip/mutual.service";
import {GetStablemateIcon} from "../components/betting-datatable";
import {CouponImage, resolveCouponImage, startCouponImageCapture} from "../coupon-image";
import "../../../shared/betslip/betslip.css";
import "../fob.betting/fob-betting.css";
import {SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";
import iconTrash from "../../../assets/feather/trash-2.svg";
import iconSave from "../../../assets/feather/save.svg";
import iconSlash from "../../../assets/feather/slash.svg";
import iconClose from "../../../assets/feather/x-circle.svg";
import iconLeft from "../../../assets/feather/chevron-left.svg";
import editIcon from "../../../assets/feather/edit.png";
import calculate from "../../../assets/feather/calculate.svg";
import blackHorse from "../../../assets/img/siyahat.png";
import {BettingTypesMap} from "features/core/definitions";

/**
 * "Hemen Oyna" geçersiz seçim yüzünden kilitliyken SEBEBİ söyler ve ÇIKIŞ YOLU verir.
 *
 * İki şey burada birleşiyor:
 *  - Sebep, at numarasıyla: "3. koşuda 1 numaralı at koşmaz durumda". Kupondaki ayrı bir
 *    kartta at ismini tekrar göstermiyoruz; ızgaradaki hücre zaten kırmızı işaretli.
 *  - Kaldırma buraya AYRI bir düğme olarak konmuyor: seçimin kendisine tıklanıp
 *    kaldırılabiliyor (bkz. `MutualBetslip.add` içindeki `isRemoval` koşulu).
 */
function MutualStaleWarning({items}: {items: MutualSlipItem[]}) {
    const rows = items.map((item) => ({
        run: item.run,
        horse: item.horse,
        reason: staleReasonOf(item),
    }));
    const summary = summarizeStale(rows);
    const text = staleSummaryText(summary);
    if (!text) {
        return null;
    }
    return (
        <div className="betslip-stale-note" role="alert">
            <span className="betslip-stale-note-icon" aria-hidden="true">!</span>
            <span>{text}</span>
        </div>
    );
}

const STALE_ITEM_ERROR = "Kuponunuzda artık geçerli olmayan bir seçim var, lütfen kaldırın.";

function simpleView(legs: any[]): string {
    const str: any[] = [];
    legs.forEach((l: any, i: number) => {
        if (i === 0) {
            str.push(l);
        } else if (Number(l) === Number(legs[i - 1]) + 1 && Number(l) === Number(legs[i + 1]) - 1) {
            if (str[str.length - 1] !== "-") {
                str.push("-");
            }
        } else {
            str.push(l);
        }
    });
    return str.join(", ").replaceAll(", -, ", "-");
}

function buildMultiplyChoices(maxMultiply: number): number[] {
    let choices: number[] = [];
    if (maxMultiply > 200) {
        choices = _.range(1, 101);
        const mod = maxMultiply % 50;
        choices.push(..._.range(200, maxMultiply, 50));
        if (mod > 0) {
            choices.push(choices[choices.length - 1] + mod);
        }
    } else {
        choices = _.range(1, maxMultiply + 1);
    }
    return choices;
}

function quickFill(mode: "agf" | "ganyan" | "mix", options: MutualSlipOptions, agfRatioField: string) {
    const {hippodrome, bettingType, bettingSubType} = options;
    if (!hippodrome || !bettingType || !bettingSubType) {
        return;
    }

    MutualBetslip.clear();

    const labelParts = String(bettingSubType.label).split("-");
    for (let i = 0; i < bettingType.column; i += 1) {
        const runNo = labelParts.length > 1 ? Number(labelParts[i]) : Number(bettingSubType.id);
        const runObject = hippodrome.runs.find((r: any) => Number(r.no) === runNo);
        if (!runObject) {
            continue;
        }

        const agfHorses = _.orderBy(runObject.horses, [agfRatioField], ["desc"]);
        const minGanyan = _.orderBy(runObject.horses, ["ganyan"], ["asc"]);

        const picks: any[] = [];
        if (mode === "agf") {
            picks.push(agfHorses[0], agfHorses[1]);
        } else if (mode === "ganyan") {
            picks.push(minGanyan[0], minGanyan[1]);
        } else {
            picks.push(minGanyan[0], minGanyan[1]);
            if (!picks.includes(agfHorses[0])) {
                picks.push(agfHorses[0]);
            }
            if (!picks.includes(agfHorses[1])) {
                picks.push(agfHorses[1]);
            }
        }

        for (const horse of picks) {
            if (horse) {
                MutualBetslip.add(i + 1, runObject, horse, "toggle");
            }
        }
    }
}

function buildConfirmModalContent(
    options: MutualSlipOptions,
    betCount: number,
    totalAmount: number,
    system: boolean,
    systemMaxMin: number[] | null,
    slipItem: MutualSlipItem[],
    horsesMap: any
): string {
    const {hippodrome, bettingType, bettingSubType} = options;
    const price = Number(bettingType.price);
    const isSorted = bettingType.isCommaType;

    const betCountText =
        system && systemMaxMin
            ? systemMaxMin[0] !== systemMaxMin[1]
                ? systemMaxMin.join("-")
                : String(systemMaxMin[0])
            : String(betCount);

    const amountText =
        system && systemMaxMin
            ? systemMaxMin[0] !== systemMaxMin[1]
                ? `${CurrencyFormat(systemMaxMin[0] * price)}₺ - ${CurrencyFormat(systemMaxMin[1] * price)}₺`
                : `${CurrencyFormat(systemMaxMin[0] * price)}₺`
            : `${CurrencyFormat(totalAmount)}₺`;

    const uniqueColumn = [...Array.from(new Set(slipItem.map((i: any) => i.column)))].sort();

    const columnRunMap: any = {};
    for (const col of uniqueColumn) {
        columnRunMap[Number(col)] = slipItem.find((r: any) => r.column === col);
    }

    const legsHtml = uniqueColumn
        .map((col: any) => {
            const rows = _.orderBy(slipItem, [(el: any) => (el.horse === "s" ? 0 : 1)])
                .filter((h: any) => h.column === col)
                .map((item: any, key: number) => {
                    const horse = horsesMap[columnRunMap[col]?.run]?.[Number(item.horse)];
                    const agfClass =
                        horse?.agf_sort_no1 === 1 || horse?.agf_sort_no2 === 1 ? "agf-horse border-0" : "";
                    const noText = isSorted ? `${key + 1}.At` : item.horse === "s" ? "S" : (horse?.no ?? item.horse);
                    const nameText = item.horse === "s" ? "HAZIR BAHİS" : (horse?.name ?? "");

                    return `<div class="horse-no d-flex">
                        <div class="text-center ${agfClass}" style="font-weight: bold">${noText}</div>
                        <div class="text-left text-wrap ps-2 ${agfClass}">${isSorted ? item.horse : ""} ${nameText}</div>
                    </div>`;
                })
                .join("");

            const columnLabel = String(bettingType.columnLabel ?? "").toUpperCase();
            const columnNo = bettingType.columnLabel === "Koşu" ? bettingSubType.id : col;

            return `<div class="col-12" style="width: ${
                isMobile ? "" : `calc(100% / ${uniqueColumn.length})`
            };flex: unset;max-width: unset;">
                <div class="run-no">${columnNo}. ${columnLabel}</div>
                ${rows}
            </div>`;
        })
        .join("");

    return `
        <table class="table table-striped coupon-detail">
            <tbody>
                <tr>
                    <th class="text-left" style="width: 100px">Hipodrom</th>
                    <td class="text-left" style="width: calc(15% - 120px)">${hippodrome.location}</td>
                    <th class="text-left" style="width: 100px">Bahis Türü</th>
                    <td class="text-left" style="width: calc(25% - 120px)">${bettingType.name}</td>
                    ${
                        !isMobile
                            ? `<th class="text-left" style="width: 100px">Koşu</th>
                               <td class="text-left" style="width: calc(25% - 120px)">${bettingSubType.label}</td>`
                            : ""
                    }
                </tr>
                <tr>
                    <th class="text-left" style="width: 100px">Bahis Sayısı</th>
                    <td class="text-left" style="width: calc(15% - 120px)">${betCountText}</td>
                    <th class="text-left" style="width: 100px">Misli</th>
                    <td class="text-left" style="width: calc(25% - 120px)">${options.betSlipMultiply}</td>
                    ${
                        !isMobile
                            ? `<th class="text-left" style="width: 100px">Tutar</th>
                               <td class="text-left" style="width: calc(25% - 120px)">${amountText}</td>`
                            : ""
                    }
                </tr>
                ${
                    isMobile
                        ? `<tr>
                               <th class="text-left" style="width: 100px">Koşu</th>
                               <td class="text-left" style="width: calc(25% - 120px)">${bettingSubType.label}</td>
                               <th class="text-left" style="width: 100px">Tutar</th>
                               <td class="text-left" style="width: calc(25% - 120px)">${amountText}</td>
                           </tr>`
                        : ""
                }
            </tbody>
        </table>

        <div class="coupon-legs" style="width:auto; margin-top: 15px;">${legsHtml}</div>
    `;
}

const SortableDragHandle = SortableHandle(({children}: any) => children);

const SortableHorseItem: any = SortableElement(({position, label, isUnresolvable, onRemove}: any) => (
    <li style={isUnresolvable ? {border: "1px solid var(--bs-danger)"} : undefined}>
        <SortableDragHandle>
            <span>
                {position + 1}. {label}
            </span>
        </SortableDragHandle>
        <img
            alt={"sil"}
            src={iconTrash}
            width={14}
            onClick={onRemove}
            className="cursor-pointer float-right"
        />
    </li>
));

const SortableHorseList: any = SortableContainer(({rows}: any) => (
    <ul className="sortable-horses">
        {rows.map((row: any, index: number) => (
            <SortableHorseItem
                key={`sorted-slip-${index}`}
                index={index}
                position={index}
                label={row.label}
                isUnresolvable={row.isUnresolvable}
                onRemove={row.onRemove}
            />
        ))}
    </ul>
));

export function NewMutualBetslip({onClose}: any) {
    const navigate = useNavigate();
    useMutualBetslip();
    const options = useMutualSlipOptions();

    const [profitModal, setProfitModal] = useState<boolean>(false);
    const [profitHorses, setProfitHorses] = useState<any>({});
    const errorClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const slipItem = MutualBetslip.getCurrentItems();
    const betCount = MutualBetslip.getBetCount();
    const totalAmount = MutualBetslip.getTotalAmount();
    const maxMultiply = MutualBetslip.getMaxMultiply();
    const {system, systemMaxMin} = MutualBetslip.getSystemState();
    const betSlipLegs = MutualBetslip.getLegsSelections();

    const hasStaleItem = slipItem.some((item) => !isMutualItemPlayable(item));
    const staleItems = slipItem.filter((item) => !isMutualItemPlayable(item));
    const staleKeys = new Set(staleItems.map((item) => `${item.column}-${item.run}-${item.horse}`));

    const bettingType = options.bettingType;
    const bettingSubType = options.bettingSubType;
    const hippodrome = options.hippodrome;

    const maxCouponAmount = Number(bettingType?.maxCouponAmount ?? 0);
    const maxAmountError = "Toplam bahis tutarı " + CurrencyFormat(maxCouponAmount) + " TL'yi geçemez.";

    useEffect(() => () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
    }, []);

    const scheduleErrorClear = () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
        errorClearTimer.current = setTimeout(() => MutualBetslip.setOptions({errorMessage: null}), 5000);
    };

    useEffect(() => {
        if (maxCouponAmount > 0 && totalAmount > maxCouponAmount) {
            if (options.errorMessage !== maxAmountError) {
                MutualBetslip.setOptions({errorMessage: maxAmountError});
            }
        } else if (options.errorMessage === maxAmountError) {
            MutualBetslip.setOptions({errorMessage: null});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalAmount, maxCouponAmount]);

    useEffect(() => {
        if (bettingType && options.complete && !bettingType.isCompletable) {
            MutualBetslip.setOptions({complete: false});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bettingType?.id, options.complete]);

    if (
        !hippodrome ||
        !bettingType ||
        !bettingSubType ||
        bettingSubType.label === undefined ||
        bettingType.isPlaceholder
    ) {
        return <></>;
    }

    const subTypeLabelParts = String(bettingSubType.label).split("-");
    const runsList = hippodrome.runs.filter((run: any) => subTypeLabelParts.includes(String(run.no)));
    const runsMap: any = {};
    for (const run of runsList) {
        runsMap[Number(run.no)] = run;
    }

    const tabs: number[] = [];
    for (let i = Number(bettingSubType.id); i < Number(bettingSubType.id) + bettingType.column; i += 1) {
        tabs.push(i);
    }

    const runs = hippodrome.runs.filter((run: any) => tabs.includes(Number(run.no)));
    const maxHorseSize: number = _.max(runs.map((el: any) => el.horses.length)) ?? 0;

    const emptyTabs: number[] = [];
    for (let i = tabs.length + 1; i <= 7; i += 1) {
        emptyTabs.push(i);
    }

    const horsesMap: any = {};
    for (const run of runs) {
        horsesMap[Number(run.no)] = {};
        for (const horse of run.horses) {
            horsesMap[Number(run.no)][Number(horse.no)] = horse;
        }
    }

    const agfRatioField =
        hippodrome.secondSixStart != null && Number(bettingSubType.id) === Number(hippodrome.secondSixStart)
            ? "agf_ratio2"
            : "agf_ratio1";

    const isSortedBetType = bettingType.isCommaType;
    const sortedRun = runsMap[Number(bettingSubType.id)];
    const multiplyChoices = buildMultiplyChoices(maxMultiply);

    const profitKeys = Object.keys(profitHorses);
    const profitRatio = profitKeys.reduce(
        (acc: number, key: string) => acc * (100 / Number(profitHorses[key][agfRatioField])),
        1
    );
    const profitText =
        profitKeys.length === 6
            ? `${CurrencyFormat(profitRatio * Number(bettingType.price) * 0.4, 2)} ₺ - ${CurrencyFormat(
                profitRatio * Number(bettingType.price) * 0.6,
                2
            )} ₺`
            : "Bütün ayaklar için bir at seçiniz";

    const playCoupon = async (save: boolean = false, imgData: CouponImage = "") => {
        if (options.inProgress || slipItem.length === 0) return;

        if (!auth.member || !auth.member.id) {
            modalServiceSubject.next({
                title: "Üye Girişi",
                content: `<p>Bahis yapabilmeniz için önce oturum açmanız gerekli. Üye değilseniz hemen üye olabilir yada giriş yaparak bahis yapabilirsiniz.</p>`,
                confirm: {
                    sure: {label: "Giriş Yap", class: "btn btn-success", action: () => navigate(paths.login.url)},
                    cancel: {label: "Üye Ol", class: "btn btn-primary", action: () => navigate(paths.register.url)},
                },
            });
            return;
        }

        const processingModalTimeout = setTimeout(() => {
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Oynanıyor",
                content: '<div class="text-center"><div class="loader"></div><p>Kuponunuz oynanmak üzere United Racing sistemine iletildi. United Racing sisteminden yanıt gelene kadar lütfen bekleyiniz.</p></div>',
                confirm: null,
                backdropCloseDisabled: true,
            });
        }, 100);

        const result = await MutualBetslip.submit(save, await resolveCouponImage(imgData));
        clearTimeout(processingModalTimeout);

        if (!result) {
            setTimeout(() => {
                modalServiceSubject.next(false);
            }, 1500);
            return;
        }

        if (result.status === true && save === false) {
            try {
                // @ts-ignore
                fbq("track", "Purchase", {value: totalAmount, currency: "TRY"});
            } catch (e) {
            }
            MutualBetslip.clear();
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Oynandı",
                content: `<p class="text-center"><b>${result.data.coupon_data.approve_code
                }</b> kodu ile <b>${CurrencyFormat(Number(result.data.coupon_data.amount || 0))} ₺</b> tutarındaki <b>${BettingTypesMap[Number(result.data.coupon_data.bet_type)]?.name
                }</b> kuponunuz başarıyla oynandı. <br/>Kuponunuzun durumunu kuponlarım sayfasından takip edebilirsiniz.</p>`,
                confirm: {
                    sure: {label: "At Yarışı Kuponlarım", action: () => navigate(paths.memberTickets.url)},
                    cancel: {label: "Yeni Kupon Oyna", action: () => { if (isMobile) onClose?.(); }},
                },
            });
        } else if (result.status && save === true) {
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Kaydedildi",
                content: "Kuponunuz başarıyla Kaydedildi. Kuponunuzu kuponlarım sayfasında görüntüleyebilirsiniz",
                confirm: {
                    sure: {label: "Kayıtlı Kuponlarım", action: () => navigate(paths.memberSavedTickets.url)},
                    cancel: {label: "Yeni Kupon Oyna", action: () => { if (isMobile) onClose?.(); }},
                },
            });
        } else {
            let errorDescription: any = null;
            let errorMessageCode: any = null;
            if (result.data && result.data.error) {
                errorDescription = result.data.error.error_description;
                errorMessageCode = result.data.error.error_message;
            }

            let title: any = "Hata";
            let sure: any = null;
            if (typeof result.message === "string" && result.message.indexOf("Oturum") !== -1) {
                sure = {label: "Giriş Yap", action: () => navigate(paths.login.url)};
            }
            if (errorMessageCode === "TIMEOUT") {
                title = "Dikkat";
                sure = {label: "At Yarışı Kuponlarım", action: () => navigate(paths.memberTickets.url)};
            }

            modalServiceSubject.next({
                case: "danger",
                title: title,
                content: errorDescription || (typeof result.message === "object" ? JSON.stringify(result.message) : result.message),
                confirm: {sure, cancel: {label: "Tamam"}},
            });
        }
    };

    const confirmCoupon = async () => {
        if (!(auth.authentication && auth.member && auth.member.id)) {
            navigate(`${paths.login.url}/${window.location.pathname.replaceAll("/", "|")}`);
            return;
        }

        const payableAmount = system ? Number(options.maxAmount) : totalAmount;
        const minCouponAmount = Number(bettingType.minCouponAmount ?? 0);

        let errorMessage: string | null = null;

        if (betCount === 0) {
            errorMessage = "Kuponunuz da oynanacak bir yarış bulunmamaktadır.";
        } else if (hasStaleItem) {
            errorMessage = STALE_ITEM_ERROR;
        } else if (payableAmount > 12000) {
            errorMessage = "Kupon tutarı 12000 TL'den fazla olamaz.";
        } else if (
            options.shareCoupon !== true &&
            (options.usePointBalance
                ? Number(auth.member.balance_debit) + Number(auth.member.balance_point)
                : Number(auth.member.balance_debit)) < payableAmount
        ) {
            errorMessage = "Bakiyeniz yetersiz.";
        } else if (
            (system && systemMaxMin ? systemMaxMin[1] * Number(bettingType.price) : totalAmount) < minCouponAmount
        ) {
            errorMessage =
                "Minumum bahis tutarı " +
                CurrencyFormat(bettingType.minCouponAmount ? bettingType.minCouponAmount : 10) +
                " TL olmalıdır.";
        }

        if (errorMessage) {
            MutualBetslip.setOptions({errorMessage});
            scheduleErrorClear();
            return;
        }

        // Görüntü onay modalı açılmadan ÖNCE başlatılıyor (modal görüntüye girmesin) ama
        // BEKLENMİYOR: anlık görüntü çağrı anında alınıyor, geri kalanı modal açıkken bitiyor.
        // Doğrulamadan SONRA: hatalı kuponda bu iş boşuna yapılıyordu.
        const imgData = startCouponImageCapture("legs-row");

        if (isMobile) {
            playCoupon(false, imgData);
            return;
        }

        modalServiceSubject.next({
            title: `BAHİS DETAYI - ${hippodrome.location} - (${dateTimeStringFormat(hippodrome.date, true)})`,
            width: "960px",
            content: buildConfirmModalContent(options, betCount, totalAmount, system, systemMaxMin, slipItem, horsesMap),
            confirm: {
                sure: {class: "btn-success", label: "Onayla", action: () => playCoupon(false, imgData)},
                cancel: {label: "Vazgeç"},
            },
        });

        scheduleErrorClear();
    };

    return (
        <>
            <div className="betslip" id={"betslip"}>
                {isMobile && (
                    <button
                        className="btn btn-primary btn-icon float-start py-1 px-1 me-2"
                        onClick={() => onClose?.()}
                    >
                        <img alt={"left"} src={iconLeft} className="invert-color me-1"/>
                    </button>
                )}

                <h2 className="betting-type">
                    {bettingType.isSecondSixCapable && hippodrome.secondSixStart != null && (
                        <>{agfRatioField === "agf_ratio2" ? "2. " : "1. "}</>
                    )}
                    {bettingType.name}

                    {bettingType.info && (
                        <i
                            className="info-circle float-end"
                            onClick={() =>
                                modalServiceSubject.next({
                                    case: "info",
                                    title: bettingType.name,
                                    content: bettingType.info,
                                    confirm: {cancel: {label: "Tamam", class: "btn-warning fw-semibold w-100"}},
                                })
                            }
                        >
                            i
                        </i>
                    )}
                    {!isMobile && onClose && (
                        <img
                            alt={"kapat"}
                            src={iconClose}
                            className="float-end cursor-pointer me-1"
                            onClick={() => onClose()}
                        />
                    )}
                </h2>

                <h3 className="hippodrome">{hippodrome.hippodrome}</h3>

                {bettingType.isSecondSixCapable && (
                    <div className={"d-flex"}>
                        <div className="dropdown w-100">
                            <button
                                className={`btn btn-sm btn-six dropdown-toggle w-100 d-flex justify-content-center align-items-center fw-bold`}
                                data-bs-toggle="dropdown"
                                style={{color: "#FFFFFF"}}
                                aria-expanded="false"
                            >
                                <img className={"me-1 invert-color"} src={editIcon} width={14} alt={"hizli"}/> Hızlı 6'lı
                                Yap
                            </button>
                            <ul className="dropdown-menu bg-white w-100">
                                <li className={"w-100"}>
                                    <a
                                        className="dropdown-item text-start"
                                        href="#"
                                        onClick={() => quickFill("agf", options, agfRatioField)}
                                    >
                                        AGF'ye Göre Doldur
                                    </a>
                                </li>
                                <li className={"w-100"}>
                                    <a
                                        className="dropdown-item text-start"
                                        href="#"
                                        onClick={() => quickFill("ganyan", options, agfRatioField)}
                                    >
                                        Ganyan'a Göre Doldur
                                    </a>
                                </li>
                                <li className={"w-100"}>
                                    <a
                                        className="dropdown-item text-start"
                                        href="#"
                                        onClick={() => quickFill("mix", options, agfRatioField)}
                                    >
                                        AGF + Ganyan'a Göre Doldur
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}

                {isSortedBetType ? (
                    <div className="body betslip-body flex-column" id={"betslip-body"}>
                        <select
                            className="w-100 mb-2"
                            value={"-1"}
                            onChange={(e) => {
                                const horse = sortedRun?.horses.find((h: any) => String(h.no) === e.target.value);
                                if (horse) {
                                    MutualBetslip.add(1, sortedRun, horse, "add");
                                }
                            }}
                        >
                            <option value={"-1"}>At Seçiniz</option>
                            {sortedRun?.horses
                                .filter(
                                    (horse: any) =>
                                        horse.run_status === true &&
                                        MutualBetslip.isAdded(1, sortedRun, horse) === false
                                )
                                .map((horse: any, key: number) => (
                                    <option key={`sorted-horse-${key}`} value={String(horse.no)}>
                                        {horse.no} - {horse.name}
                                    </option>
                                ))}
                        </select>

                        <SortableHorseList
                            useDragHandle={true}
                            helperClass={"sortable-horse-helper"}
                            onSortEnd={({oldIndex, newIndex}: any) => MutualBetslip.reorder(oldIndex, newIndex)}
                            rows={slipItem.map((item: MutualSlipItem) => {
                                const horse = sortedRun?.horses.find((h: any) => String(h.no) === String(item.horse));
                                return {
                                    label: `${horse?.no ?? item.horse} - ${horse?.name ?? ""}`,
                                    isUnresolvable: !isMutualItemPlayable(item),
                                    onRemove: () => MutualBetslip.remove(item.column, {no: item.run}, {no: item.horse}),
                                };
                            })}
                        />
                    </div>
                ) : (
                    <div className="body betslip-body" id={"betslip-body"}>
                        {tabs.map((column: any, keyColumn: number) => {
                            const runObject =
                                runsMap[
                                    subTypeLabelParts.length > 1
                                        ? Number(subTypeLabelParts[keyColumn])
                                        : Number(bettingSubType.id)
                                    ];
                            const horses = runObject?.horses;
                            const isAddedAllStatus = runObject ? MutualBetslip.isAddedAll(keyColumn + 1, runObject) : false;
                            const isSystemAdded = runObject
                                ? MutualBetslip.isAdded(keyColumn + 1, runObject, "s") !== false
                                : false;
                            const isSystemStale = staleKeys.has(`${keyColumn + 1}-${Number(runObject?.no)}-s`);

                            return (
                                <ul key={`betslip-columns-${keyColumn}`}>
                                    <li
                                        className="column-no"
                                        onClick={() => MutualBetslip.setOptions({column: keyColumn + 1})}
                                    >
                                        {keyColumn + 1}
                                        {isMobile && <>. {bettingType.columnLabel}</>}
                                    </li>

                                    {subTypeLabelParts.length > 1 && (
                                        <li
                                            className={`system ${isSystemAdded ? "horse-added" : ""}`}
                                            style={isSystemStale ? {border: "1px solid var(--bs-danger)"} : undefined}
                                            onClick={() => MutualBetslip.add(keyColumn + 1, runObject, "s", "toggle")}
                                        >
                                            {isMobile ? "Sistem" : "S"}
                                        </li>
                                    )}

                                    {!isMobile && (
                                        <li
                                            onClick={() => {
                                                MutualBetslip.addAll(
                                                    keyColumn + 1,
                                                    runObject,
                                                    isAddedAllStatus === false ? "add" : "remove"
                                                );
                                                MutualBetslip.add(keyColumn + 1, runObject, "s", "remove");
                                            }}
                                            className={`all ${isAddedAllStatus === true ? "selected-all" : ""}`}
                                        >
                                            H
                                        </li>
                                    )}

                                    {isMobile && betSlipLegs[keyColumn]?.length === 0 && (
                                        <li className="bg-dark-subtle">
                                            <i className="d-block text-center">Bu ayak için en az 1 at seçmelisiniz!</i>
                                        </li>
                                    )}

                                    {horses &&
                                        horses.map((horse: any, keyHorses: number) => {
                                            const horseIsAdded =
                                                MutualBetslip.isAdded(keyColumn + 1, runObject, horse) !== false;
                                            if (isMobile && !horseIsAdded) {
                                                return null;
                                            }
                                            const isUnresolvable = staleKeys.has(
                                                `${keyColumn + 1}-${Number(runObject?.no)}-${String(horse.no)}`
                                            );

                                            return (
                                                <li
                                                    key={`betslip-columns-horses-${keyHorses}`}
                                                    className={`${!isMobile && horseIsAdded ? "horse-added" : ""} ${
                                                        horse.agf_sort_no1 === 1 || horse.agf_sort_no2 === 1
                                                            ? "agf-horse"
                                                            : ""
                                                    }`}
                                                    style={isUnresolvable ? {border: "1px solid var(--bs-danger)"} : undefined}
                                                    onClick={() => {
                                                        if (!isMobile) {
                                                            MutualBetslip.add(keyColumn + 1, runObject, horse, "toggle");
                                                        }
                                                    }}
                                                >
                                                    {horse.run_status !== true ? (
                                                        <img src={iconSlash} alt="/"/>
                                                    ) : (
                                                        <>
                                                            {horse.no}
                                                            {isMobile && (
                                                                <>
                                                                    {" "}
                                                                    - {horse.name} <GetStablemateIcon horse={horse}/>{" "}
                                                                    <i className={"float-end"}>
                                                                        <img
                                                                            alt={"sil"}
                                                                            src={iconTrash}
                                                                            width={14}
                                                                            onClick={() =>
                                                                                MutualBetslip.add(
                                                                                    keyColumn + 1,
                                                                                    runObject,
                                                                                    horse,
                                                                                    "toggle"
                                                                                )
                                                                            }
                                                                            className="cursor-pointer float-right"
                                                                        />
                                                                    </i>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </li>
                                            );
                                        })}

                                    {maxHorseSize > (horses?.length ?? 0) &&
                                        [...Array(maxHorseSize - (horses?.length ?? 0))].map(
                                            (el: any, keyEmptyHorses: number) => (
                                                <li
                                                    className="emptyHorse"
                                                    key={`betslip-columns-empty-horses-${keyEmptyHorses}`}
                                                />
                                            )
                                        )}
                                </ul>
                            );
                        })}

                        {!isMobile &&
                            emptyTabs.map((emptyColumn: number, emptyColumnKey: number) => (
                                <ul className="emptyColumn" key={`betslip-emptyColumns-${emptyColumnKey}`}>
                                    <li className="column-no">{emptyColumn}</li>
                                    {subTypeLabelParts.length > 1 && <li className="system">S</li>}
                                    <li className="all">H</li>
                                    {[...Array(maxHorseSize)].map((el: any, keyEmptyHorses: number) => (
                                        <li className="emptyHorse" key={`betslip-empty-horses-${keyEmptyHorses}`}/>
                                    ))}
                                </ul>
                            ))}
                    </div>
                )}

                <div className="footer">
                    <div style={{border: 0, paddingTop: 0}} id={"legs-row"}>
                        <div className="d-flex w-100">
                            {betSlipLegs
                                ?.map((l: any) => (isSortedBetType ? l : simpleView(l)))
                                .filter((e: any) => e !== "")
                                .join(" / ")}
                        </div>
                    </div>

                    {bettingType.isCompletable && (
                        <div>
                            <label>Komple:</label>
                            <input
                                type="checkbox"
                                checked={options.complete}
                                onChange={() => MutualBetslip.setOptions({complete: !options.complete})}
                            />
                        </div>
                    )}

                    {system ? (
                        <div>
                            <label>Tutar:</label>
                            <input
                                type="number"
                                style={{textAlign: "right"}}
                                pattern="^[0-9\b]+$"
                                min={1}
                                max={maxMultiply}
                                step={1}
                                value={options.maxAmount || ""}
                                onChange={(e) =>
                                    MutualBetslip.setOptions({
                                        maxAmount:
                                            e.target.value === ""
                                                ? ""
                                                : Number(e.target.value) > 0
                                                    ? Number(e.target.value)
                                                    : 1,
                                    })
                                }
                                onBlur={() => {
                                    if (options.maxAmount === "") {
                                        MutualBetslip.setOptions({maxAmount: 1});
                                    }
                                }}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    return false;
                                }}
                            />
                        </div>
                    ) : (
                        <div>
                            <label>Misli:</label>
                            <div className={"betslip-misli"}>
                                <input
                                    type="number"
                                    className={"betslip-input"}
                                    style={{textAlign: "left"}}
                                    pattern="^[0-9\b]+$"
                                    min={1}
                                    max={maxMultiply}
                                    step={1}
                                    dir="ltr"
                                    value={options.betSlipMultiply || ""}
                                    onKeyDown={preventBetSlipValues}
                                    onChange={(e) =>
                                        MutualBetslip.setOptions({
                                            betSlipMultiply: e.target.value,
                                            errorMessage: null,
                                        })
                                    }
                                    onBlur={() => {
                                        if (options.betSlipMultiply === "") {
                                            MutualBetslip.setOptions({betSlipMultiply: 1});
                                        } else if (Number(options.betSlipMultiply) > maxMultiply) {
                                            MutualBetslip.setOptions({betSlipMultiply: maxMultiply});
                                        }
                                    }}
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        return false;
                                    }}
                                />
                                <select
                                    className="betslip-select"
                                    value={options.betSlipMultiply}
                                    onChange={(e) =>
                                        MutualBetslip.setOptions({
                                            betSlipMultiply: Number(e.target.value),
                                            errorMessage: null,
                                        })
                                    }
                                >
                                    {multiplyChoices.map((el: number, key: number) => (
                                        <option key={key} value={el}>
                                            {el}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div>
                        <label>Bahis:</label>
                        <span>
                            {system && systemMaxMin ? (
                                <>{systemMaxMin[0] !== systemMaxMin[1] ? systemMaxMin.join("-") : systemMaxMin[0]}</>
                            ) : (
                                <>{betCount}</>
                            )}
                        </span>
                    </div>

                    <div>
                        <label>Tutar:</label>
                        <span>
                            {system && systemMaxMin ? (
                                systemMaxMin[0] === systemMaxMin[1] ? (
                                    <>{CurrencyFormat(systemMaxMin[0] * Number(bettingType.price))}₺</>
                                ) : (
                                    <>
                                        {CurrencyFormat(systemMaxMin[0] * Number(bettingType.price))}₺ -{" "}
                                        {CurrencyFormat(systemMaxMin[1] * Number(bettingType.price))}₺
                                    </>
                                )
                            ) : (
                                <>{CurrencyFormat(totalAmount)}₺</>
                            )}
                        </span>
                    </div>

                    {bettingType.isSecondSixCapable && (
                        <div>
                            <label
                                onClick={() => setProfitModal(true)}
                                style={{width: "100%"}}
                                className={"cursor-pointer"}
                            >
                                <img src={calculate} width={21} height={"auto"} className={"me-1"} alt={"hesapla"}/>
                                Bu 6'lı Ne Verir?
                            </label>
                        </div>
                    )}

                    {auth.authentication && auth.member && auth.member.id ? (
                        <div>
                            <label>Puan Kullan:</label>
                            <span>
                                <input
                                    type="checkbox"
                                    checked={options.usePointBalance}
                                    disabled={auth.member ? auth.member.balance_point <= 0 : true}
                                    onChange={() =>
                                        MutualBetslip.setOptions({usePointBalance: !options.usePointBalance})
                                    }
                                />{" "}
                                {auth.member ? auth.member.balance_point + "₺" : "0.00₺"}
                            </span>
                        </div>
                    ) : null}

                    {options.errorMessage ? (
                        <div className="mt-1 mb-1">
                            <div className="w-100 p-2 alert alert-danger m-0">{options.errorMessage}</div>
                        </div>
                    ) : null}

                    {auth.authentication && auth.member && auth.member.is_editor === true && (
                        <div>
                            <label>Paylaş:</label>
                            <span>
                                <input
                                    type="checkbox"
                                    checked={options.shareCoupon}
                                    onChange={() => MutualBetslip.setOptions({shareCoupon: !options.shareCoupon})}
                                />
                            </span>
                        </div>
                    )}

                    {options.inProgress ? (
                        <div className="actions">
                            <button className="button-clear" disabled>
                                <img src={iconTrash} alt=""/>
                            </button>
                            <button disabled className="button-save">
                                <img src={iconSave} alt=""/>
                            </button>
                            <button className="button-play" disabled>
                                Hemen Oyna
                            </button>
                        </div>
                    ) : (
                        <>
                        <MutualStaleWarning items={slipItem}/>
                        <div className="actions">
                            <button className="button-clear" onClick={() => MutualBetslip.clear()}>
                                <img src={iconTrash} alt=""/>
                            </button>

                            <button
                                onClick={() => playCoupon(true)}
                                className="button-save"
                                disabled={slipItem.length === 0}
                            >
                                <img src={iconSave} alt=""/>
                            </button>

                            {!options.shareCoupon && hippodrome.status && (
                                <button
                                    disabled={
                                        options.inProgress ||
                                        !!options.errorMessage ||
                                        (system && systemMaxMin?.[0] === 0) ||
                                        betCount === 0 ||
                                        hasStaleItem
                                    }
                                    className={`button-play`}
                                    onClick={() => confirmCoupon()}
                                >
                                    Hemen Oyna
                                </button>
                            )}

                            {!hippodrome.status && (
                                <button className="button-play" disabled>
                                    Bahisler Açılmadı
                                </button>
                            )}

                            {options.shareCoupon && (
                                <button
                                    disabled={
                                        !!options.errorMessage ||
                                        (system && systemMaxMin?.[0] === 0) ||
                                        betCount === 0 ||
                                        hasStaleItem
                                    }
                                    className={`button-play share`}
                                    onClick={() => confirmCoupon()}
                                >
                                    Hemen Paylaş
                                </button>
                            )}
                        </div>
                        </>
                    )}
                </div>
            </div>

            {bettingType.isSecondSixCapable && (
                <Modal
                    isOpen={profitModal}
                    onRequestClose={() => setProfitModal(false)}
                    onAfterClose={() => setProfitHorses({})}
                    ariaHideApp={false}
                    style={{
                        content: {
                            top: "50%",
                            left: "50%",
                            right: "auto",
                            bottom: "auto",
                            width: "1140px",
                            maxHeight: "calc(100% - 40px)",
                            marginRight: "-50%",
                            maxWidth: " calc( 100% - 40px )",
                            padding: "10px",
                            transform: "translate(-50%, -50%)",
                            background: "var(--bs-white)",
                            borderRadius: "var(--bs-border-radius-xl)",
                            border: 0,
                        },
                        overlay: {
                            zIndex: 2,
                            backgroundColor: "rgba(0, 0, 0, 0.25)",
                            backdropFilter: "blur(10px)",
                        },
                    }}
                    contentLabel=""
                >
                    <div className="card mt-0">
                        <div className="title modal-sticky-title">
                            <div className={"float-end"}>
                                <img
                                    alt={"iconExit"}
                                    src={iconClose}
                                    width={24}
                                    className={"float-end cursor-pointer"}
                                    onClick={() => setProfitModal(false)}
                                />
                            </div>
                            <h3>İkramiye Hesaplama</h3>
                        </div>

                        {profitModal && (
                            <div className="body">
                                <div className={`coupon-legs row`}>
                                    {runsList.map((el: any, key: number) => (
                                        <div
                                            key={"tickets-" + key}
                                            className={`${isMobile ? "col-12 mt-1" : "col-4 mt-2"}`}
                                        >
                                            <div className="run-no d-flex justify-content-between px-3">
                                                {key + 1} . {bettingType.columnLabel}
                                            </div>
                                            {el.horses
                                                .filter(
                                                    (item: any) => MutualBetslip.isAdded(key + 1, el, item) !== false
                                                )
                                                .map((item: any, horseKey: number) => (
                                                    <div
                                                        key={`order-detail-horse-${horseKey}`}
                                                        className="horse-no d-flex justify-content-between mt-1 align-items-center"
                                                        style={mobileView ? {background: "var(--color-bg-white)"} : {}}
                                                    >
                                                        <div className={"text-left w-50"} style={{paddingLeft: 5}}>
                                                            {item.no} - {item.name}
                                                        </div>
                                                        <div
                                                            style={{paddingRight: 5}}
                                                            className={"text-end fw-bold flex-fill"}
                                                        >
                                                            %{item[agfRatioField]}
                                                        </div>
                                                        <div
                                                            style={{paddingRight: 10}}
                                                            className={"text-end flex-fill"}
                                                        >
                                                            <label className="switch">
                                                                <input
                                                                    className="profit-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    checked={profitHorses[key]?.no === item.no}
                                                                    onChange={() =>
                                                                        setProfitHorses((prev: any) => ({
                                                                            ...prev,
                                                                            [key]: item,
                                                                        }))
                                                                    }
                                                                />
                                                                <span className="slider round"></span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className={"footer sticky-bottom bg-white px-2 py-3"} style={{bottom: "-15px"}}>
                            <p className={`${isMobile ? "fs-6" : "fs-5"}`}>
                                Tahmini İkramiye: <b>{profitText}</b>
                            </p>
                            <p className="my-1 alert alert-warning">
                                <b>"Bu 6'lı Ganyan Ne Verir?"</b> özelliği ile hesaplanan ikramiye tutarı, tahmini
                                olarak hesaplanmaktadır. Resmi ikramiye tutarı, paylaşılan resmi sonuçların ilanından
                                sonra belli olur.
                            </p>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

export function NewMutualBetslipToggleTab({onOpen}: { onOpen: () => void }) {
    useMutualBetslip();
    const slipItem = MutualBetslip.getCurrentItems();
    const betCount = MutualBetslip.getBetCount();
    const {system, systemMaxMin} = MutualBetslip.getSystemState();

    return (
        <div onClick={onOpen} className={"fob-toggle py-1"}>
            <div className="d-flex align-items-center justify-content-start ">
                <span className={`fob-toggle-icon`}>
                    <span className="horse-amount">{slipItem.length}</span>
                    <img src={blackHorse} alt=""/>
                    <span className="up-icon">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="30"
                            height="30"
                            fill="currentColor"
                            className="bi bi-caret-up"
                            viewBox="0 0 16 16"
                        >
                            <path
                                d="M3.204 11h9.592L8 5.519zm-.753-.659 4.796-5.48a1 1 0 0 1 1.506 0l4.796 5.48c.566.647.106 1.659-.753 1.659H3.204a1 1 0 0 1-.753-1.659"/>
                        </svg>
                    </span>
                </span>
                <div className="d-block justify-content-start ms-0">
                    <span className="fw-light">Bahis:</span>
                    <span className="nowrap">
                        {system && systemMaxMin
                            ? systemMaxMin[0] !== systemMaxMin[1]
                                ? systemMaxMin.join("-")
                                : systemMaxMin[0]
                            : betCount}
                    </span>
                </div>
            </div>
        </div>
    );
}

export function NewMutualMobileBetslipBar({onOpen}: { onOpen: () => void }) {
    useMutualBetslip();
    const options = useMutualSlipOptions();

    const betCount = MutualBetslip.getBetCount();
    const totalAmount = MutualBetslip.getTotalAmount();
    const {system, systemMaxMin} = MutualBetslip.getSystemState();
    const betSlipLegs = MutualBetslip.getLegsSelections();

    const bettingType = options.bettingType;
    const bettingSubType = options.bettingSubType;
    const hippodrome = options.hippodrome;

    if (!hippodrome || !bettingType || !bettingSubType) {
        return <></>;
    }

    const agfRatioField =
        hippodrome.secondSixStart != null && Number(bettingSubType.id) === Number(hippodrome.secondSixStart)
            ? "agf_ratio2"
            : "agf_ratio1";

    return (
        <div id="mobile-foot-betslip" className="mobile-foot-betslip sticky">
            <div className="row">
                <div className="col-4">
                    <div className={"d-flex align-items-center justify-content-between"}>
                        <h2 className={"mb-0"}>BİLETİM</h2>
                        {bettingType.isSecondSixCapable && (
                            <div className={"d-flex align-items-center"}>
                                <div className="dropdown dropup">
                                    <button
                                        className={`btn btn-sm btn-six dropdown-toggle ms-1 fw-bold`}
                                        data-bs-toggle="dropdown"
                                        style={{color: "#FFFFFF"}}
                                        aria-expanded="false"
                                    >
                                        Hızlı 6'lı
                                    </button>
                                    <ul className="dropdown-menu bg-white">
                                        <li>
                                            <a
                                                className="dropdown-item text-start"
                                                href="#"
                                                onClick={() => quickFill("agf", options, agfRatioField)}
                                            >
                                                AGF'ye Göre Doldur
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                className="dropdown-item text-start"
                                                href="#"
                                                onClick={() => quickFill("ganyan", options, agfRatioField)}
                                            >
                                                Ganyan'a Göre Doldur
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                className="dropdown-item text-start"
                                                href="#"
                                                onClick={() => quickFill("mix", options, agfRatioField)}
                                            >
                                                AGF + Ganyan'a Göre Doldur
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-8 text-end">
                    <button className="btn btn-primary float-end ms-1 fw-bold" onClick={onOpen}>
                        OYNA
                    </button>
                    BAHİS SAYISI:
                    {system && systemMaxMin ? (
                        <>{systemMaxMin[0] !== systemMaxMin[1] ? systemMaxMin.join("-") : systemMaxMin[0]}</>
                    ) : (
                        <>{betCount}</>
                    )}
                    <span className="total d-block fw-bold">
                        TUTAR:
                        {system && systemMaxMin ? (
                            systemMaxMin[0] === systemMaxMin[1] ? (
                                <>{CurrencyFormat(systemMaxMin[0] * Number(bettingType.price))}₺</>
                            ) : (
                                <>
                                    {CurrencyFormat(systemMaxMin[0] * Number(bettingType.price))}₺ -{" "}
                                    {CurrencyFormat(systemMaxMin[1] * Number(bettingType.price))}₺
                                </>
                            )
                        ) : (
                            <>{CurrencyFormat(totalAmount)}₺</>
                        )}
                    </span>
                </div>
            </div>

            <div className="row">
                <div className="d-flex py-2 pt-0 w-100 fw-bold fs-6">
                    {betSlipLegs
                        ?.map((l: any) =>
                            bettingType.isCommaType
                                ? l
                                : simpleView(l) === ""
                                    ? "-"
                                    : simpleView(l)
                        )
                        .join(" / ")}
                </div>
            </div>
        </div>
    );
}
